// Lazy imports to avoid TDZ problems
let errorHandler: any = null;
let errorHandlerModulePromise: Promise<any> | null = null;

let showToastFunction: ((type: "success" | "error" | "warning" | "info", title: string, content: string) => void) | null = null;
let isInitialized = false;

/**
 * Loads the error handler modules lazily (only when needed)
 * This avoids TDZ problems while modules are loading
 */
async function ensureErrorHandlerLoaded() {
  if (!errorHandler && !errorHandlerModulePromise) {
    // Dynamic import at runtime, not while the module loads
    errorHandlerModulePromise = Promise.all([
      import('../services/ErrorHandler'),
      import('../services/ErrorLogger')
    ]).then(([errorHandlerModule, errorLoggerModule]) => {
      errorHandler = errorHandlerModule.errorHandler;
      return { errorHandler, ErrorCategory: errorLoggerModule.ErrorCategory, ErrorSeverity: errorLoggerModule.ErrorSeverity };
    });
  }
  
  if (errorHandlerModulePromise && !errorHandler) {
    await errorHandlerModulePromise;
  }
  
  return errorHandler;
}

/**
 * Handles an error through the error handler service
 */
const handleGlobalError = async (
  error: any,
  context?: { source?: string;[key: string]: any },
  options?: { showToast?: boolean }
) => {
  const shouldToast = options?.showToast !== false;
  // Check whether the error handler is initialized
  if (!isInitialized) {
    // Fallback: log directly to the console when not initialized yet
    console.error('Global error (before initialization):', error);
    if (shouldToast && showToastFunction) {
      showToastFunction('error', 'Fehler', error?.message || 'Ein unerwarteter Fehler ist aufgetreten.');
    }
    return;
  }

  // Load the error handler modules on demand (async)
  const handler = await ensureErrorHandlerLoaded();
  if (!handler) {
    console.error('Failed to load error handler');
    return;
  }

  try {
    await handler.handleError(error, context, {
      preventBrowserError: true,
      showToast: shouldToast,
    });
  } catch (handlerError) {
    // Fallback: in case the error handler itself fails
    console.error('Error in error handler:', handlerError);
    if (showToastFunction) {
      showToastFunction('error', 'Fehler', 'Ein unerwarteter Fehler ist aufgetreten.');
    }
  }
};

/**
 * Initializes the global error handler
 */
export const initializeGlobalErrorHandler = async (
  logout: () => Promise<void>,
  navigate: (path: string) => void,
  showToast: (type: "success" | "error" | "warning" | "info", title: string, content: string) => void,
  getUserInfo?: () => { userId?: string; tenantId?: string }
) => {
  showToastFunction = showToast;

  // Load the error handler modules on demand (lazy loading)
  const handler = await ensureErrorHandlerLoaded();
  if (!handler) {
    console.error('Failed to load error handler during initialization');
    return;
  }

  // Initialize the error handler service
  handler.initialize({
    showToast,
    logout,
    navigate,
    getUserInfo: getUserInfo || (() => ({})),
  });

  isInitialized = true;

  // Register the event listeners only after initialization
  setupEventListeners();
};

/**
 * Sets up the event listeners
 * Called only after initialization, to avoid TDZ problems
 * The listeners are defined in a separate scope to make sure
 * no modules are loaded while this module loads
 */
function setupEventListeners() {
  // Define the handler functions locally, to make sure they only run at runtime
  const unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
    // Suppress the browser's default error message
    event.preventDefault();

    const error = event.reason;

    // Skip harmless React/Syncfusion render warnings.
    if (isBenignRenderWarning([error])) {
      return;
    }

    // Handle the error through our error handler
    handleGlobalError(error, {
      source: 'unhandledrejection',
      promise: true,
    });
  };

  const globalErrorHandler = (event: ErrorEvent) => {
    // Suppress the browser's default error message
    event.preventDefault();

    // Skip harmless React/Syncfusion render warnings (see isBenignRenderWarning).
    // Example: the DOMException "Node.removeChild: The node to be removed is not a child of
    // this node" from React 19 + Syncfusion grid edit popups on dialog unmount. The app keeps running.
    if (isBenignRenderWarning([event.message, event.error])) {
      return;
    }

    // Build a structured error object from the ErrorEvent
    let error: Error;
    if (event.error instanceof Error) {
      error = event.error;
    } else {
      // Create a new error object with a structured message
      const errorMessage = event.message || 'Unknown error';
      const locationInfo = event.filename 
        ? ` (${event.filename}:${event.lineno}:${event.colno})`
        : '';
      error = new Error(`${errorMessage}${locationInfo}`);
      // Keep the original error when there is one
      if (event.error) {
        (error as any).originalError = event.error;
      }
    }
    
    // Add further structured information
    (error as any).filename = event.filename;
    (error as any).lineno = event.lineno;
    (error as any).colno = event.colno;
    (error as any).source = 'dom_error';
    
    // Extend the stack trace with DOM information when there is none
    if (!error.stack && event.filename) {
      error.stack = `Error: ${error.message}\n    at ${event.filename}:${event.lineno}:${event.colno}`;
    }
    
    // Handle the error through our error handler
    handleGlobalError(error, {
      source: 'global_error',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      errorType: 'DOM_ERROR',
    });
  };

  const resourceErrorHandler = (event: ErrorEvent) => {
    // Only for resource loading errors (not for JavaScript errors)
    if (event.target && event.target !== window) {
      event.preventDefault();
      
      const target = event.target as HTMLElement;
      const resourceUrl = (target as any).src || (target as any).href || 'unknown';
      const resourceType = target.tagName || 'UNKNOWN';
      
      // Build a structured error object
      const resourceError = new Error(`Failed to load ${resourceType.toLowerCase()}: ${resourceUrl}`);
      (resourceError as any).resourceType = resourceType;
      (resourceError as any).resourceUrl = resourceUrl;
      (resourceError as any).source = 'resource_loading';
      
      handleGlobalError(resourceError, {
        source: 'resource_loading',
        resourceType: resourceType,
        resourceUrl: resourceUrl,
        errorType: 'RESOURCE_LOADING_ERROR',
      });
    }
  };

  /**
   * Handles unhandled promise rejections
   * IMPORTANT: this prevents browser error messages for unhandled rejections
   */
  window.addEventListener('unhandledrejection', unhandledRejectionHandler);

  /**
   * Handles global JavaScript errors
   * IMPORTANT: this prevents browser error messages for runtime errors
   */
  window.addEventListener('error', globalErrorHandler);

  /**
   * Handles errors while loading resources (images, scripts, and so on)
   */
  window.addEventListener('error', resourceErrorHandler, true); // useCapture = true, so that resource loading errors are caught as well

  /**
   * Overrides console.error, so that console errors are logged too
   * (optional, can be commented out when not wanted)
   */
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    // Call the original console.error (it stays visible in the browser console)
    originalConsoleError.apply(console, args);

    // Filter known harmless render warnings, so they do not surface as a user toast.
    // Example: React/Syncfusion grid and dropdown popups leave DOM nodes outside the React
    // tree on unmount; React logs "Node.removeChild: The node to be removed is not a child
    // of this node" via console.error. The app keeps working.
    if (isBenignRenderWarning(args)) {
      return;
    }

    // When it is an Error object, log it through our handler too (backend logging).
    // IMPORTANT: NO toast. Every console.error(..., errorObject) used to be turned into a
    // user toast - so a single logged error (often logged several times in the save or load
    // path) sporadically produced several error toasts. Toasts now come only from the
    // explicit error handlers (useApiErrorHandler/useErrorHandler) or from real
    // window 'error' and 'unhandledrejection' events.
    const errorArg = args.find(arg => arg instanceof Error);
    if (errorArg) {
      handleGlobalError(errorArg, {
        source: 'console_error',
      }, { showToast: false });
    }
  };
}

/**
 * Detects known harmless render warnings that the error handler should not report as a
 * user toast. The warning stays visible in the browser console (developer debugging).
 */
export function isBenignRenderWarning(args: unknown[]): boolean {
  for (const arg of args) {
    const message = arg instanceof Error
      ? `${arg.message ?? ''} ${arg.stack ?? ''}`
      : typeof arg === 'string'
        ? arg
        : null;
    if (!message) {
      continue;
    }
    if (BENIGN_RENDER_WARNING_PATTERNS.some(pattern => message.includes(pattern))) {
      return true;
    }
  }
  return false;
}

/**
 * Known harmless DOM mount/unmount race signatures from React 19 + Syncfusion
 * (grid edit popups, dropdowns, nested dialogs). The app keeps rendering normally;
 * these messages should trigger neither a user toast nor the ErrorBoundary fallback.
 * They stay visible in the browser console.
 */
export const BENIGN_RENDER_WARNING_PATTERNS = [
  'Node.removeChild',
  'removeChildFromContainer',
  'insertBefore',
  'is not a child of this node',
  'Observer.off',
];
